import {
    useCallback,
    useEffect,
    useState
} from "react";
import {
    useWindowDimensions,
    ActivityIndicator,
    TouchableOpacity,
    Image,
    View
} from "react-native";
import type {
    StatusFilter,
    EventItem
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    PageContainer,
    SelectBox,
    TextInput,
    Sticker,
    Button,
    Text
} from "ncore-ui-kit";
import {
    useNavigation
} from "@react-navigation/native";
import type RootStackParamList from "../../../../navigation/type";
import {
    type NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    SearchIcon,
    PlusIcon,
    PenIcon
} from "lucide-react-native";
import RESTServiceContext from "../../../../services/restAPI/context";
import SmartCoverImage from "../../../../components/smartCoverImage";

const EventsList = () => {
    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

    const [
        events,
        setEvents
    ] = useState<EventItem[]>([]);

    const [
        loading,
        setLoading
    ] = useState<boolean>(true);

    const [
        searchQuery,
        setSearchQuery
    ] = useState<string>("");

    const [
        statusFilter,
        setStatusFilter
    ] = useState<StatusFilter>("all");

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const fetchEvents = useCallback(async () => {
        try {
            setLoading(true);
            const result = await RESTServiceContext.action("getAllEventsForAdmin", null);
            if(result.payload) {
                setEvents(result.payload as unknown as EventItem[]);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchEvents();
    }, [
        fetchEvents
    ]);

    const getInitials = (title: string) => {
        const words = title.trim().split(" ");
        if(words.length >= 2 && words[0] && words[1]) {
            return `${words[0].charAt(0)}${words[1].charAt(0)}`.toUpperCase();
        }
        return title.slice(0, 2).toUpperCase();
    };

    const filteredEvents = events.filter(event => {
        const matchesSearch = searchQuery === "" ||
            event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            event.description.toLowerCase().includes(searchQuery.toLowerCase());

        if(!matchesSearch) {
            return false;
        }

        if(statusFilter === "standart") {
            return event.type === "standart";
        }

        if(statusFilter === "join") {
            return event.type === "join";
        }

        if(statusFilter === "members") {
            return event.isOnlyMembers;
        }

        return true;
    });

    const renderHeader = () => {
        return <View
            style={[
                stylesheet.headerContainer,
                {
                    gap: spaces.spacingMd
                }
            ]}
        >
            <Text
                variant="titleLargeSize"
            >
                {localize("events-page-title")}
            </Text>
            <Button
                onPress={() => navigation.navigate("EventCreate")}
                customTextColor={colors.project.brandWhite}
                icon={({
                    color,
                    size
                }) => <PlusIcon
                    color={colors.content.icon[color]}
                    size={size}
                />}
                title={localize("events-create-new")}
                customBorderColor="gold"
                spreadBehaviour="free"
                customColor="gold"
            />
        </View>;
    };

    const renderFilters = () => {
        return <View
            style={[
                stylesheet.filterSection,
                {
                    marginTop: spaces.spacingLg,
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={stylesheet.searchContainer}
            >
                <TextInput
                    placeholder={localize("events-search-placeholder")}
                    customColor={colors.project.brandWhite}
                    icon={({
                        color,
                        size
                    }) => <SearchIcon
                        color={colors.content.icon[color]}
                        size={size}
                    />}
                    onChangeText={setSearchQuery}
                    spreadBehaviour="stretch"
                    value={searchQuery}
                />
            </View>
            <View
                style={[
                    stylesheet.filterDropdownContainer,
                    {
                        width: isMobile ? "100%" : 220
                    }
                ]}
            >
                <SelectBox
                    onChange={selectedItems => {
                        const item = selectedItems[0];
                        if(item) {
                            setStatusFilter(item.__key as StatusFilter);
                        }
                    }}
                    data={[
                        {
                            title: localize("events-filter-all"),
                            value: "all"
                        },
                        {
                            title: localize("events-filter-standard"),
                            value: "standart"
                        },
                        {
                            title: localize("events-filter-join"),
                            value: "join"
                        },
                        {
                            title: localize("events-filter-members"),
                            value: "members"
                        }
                    ]}
                    placeholder={localize("events-filter-all")}
                    title={localize("events-filters-label")}
                    customTheme={{
                        themeKey: "events_filter" as never
                    }}
                    titleExtractor={item => item.title}
                    keyExtractor={item => item.value}
                    spreadBehaviour="stretch"
                />
            </View>
        </View>;
    };

    const renderCard = (
        event: EventItem,
        index: number
    ) => {
        const initials = getInitials(event.title);
        const eventKey = event.id || (event as unknown as { _id?: string })._id || `event-${index}`;

        return <View
            style={stylesheet.cardWrapper}
            key={eventKey}
        >
            <TouchableOpacity
                style={[
                    stylesheet.cardContainer,
                    {
                        backgroundColor: colors.project.brandWhite,
                        borderColor: colors.project.brandBorder,
                        borderWidth: borders.seperator,
                        borderRadius: radiuses.md
                    }
                ]}
                onPress={() => {
                    navigation.navigate("SubEventsList", {
                        eventID: event.eventID
                    });
                }}
                activeOpacity={0.7}
            >
                <View
                    style={[
                        stylesheet.cardBanner,
                        {
                            backgroundColor: colors.project.companyBannerBackground || "#335C57"
                        }
                    ]}
                >
                    {
                        event.coverPhotoPath ?
                            <SmartCoverImage
                                containerStyle={stylesheet.bannerImage}
                                url={event.coverPhotoPath}
                            />
                        :
                            null
                    }
                    <View
                        style={stylesheet.cardTypeBadge}
                    >
                        <Sticker
                            title={event.type === "join" ? localize("events-type-join") : localize("events-type-standard")}
                            spreadBehaviour="free"
                            customTheme={{
                                themeKey: "light"
                            }}
                            size="small"
                        />
                    </View>
                    <View
                        style={stylesheet.cardBadgeContainer}
                    >
                        <View
                            style={[
                                stylesheet.avatarBadge,
                                {
                                    backgroundColor: colors.project.brandWhite,
                                    borderColor: colors.project.brandBorder,
                                    borderWidth: borders.seperator,
                                    borderRadius: radiuses.sm
                                }
                            ]}
                        >
                            {
                                event.logoPath ?
                                    <Image
                                        style={stylesheet.avatarBadge}
                                        source={{
                                            uri: event.logoPath
                                        }}
                                        resizeMode="cover"
                                    />
                                :
                                    <Text
                                        style={[
                                            stylesheet.avatarBadgeText,
                                            {
                                                color: colors.project.gold
                                            }
                                        ]}
                                        variant="titleMediumSize"
                                    >
                                        {initials}
                                    </Text>
                            }
                        </View>
                    </View>
                </View>
                <View
                    style={[
                        stylesheet.cardBody,
                        {
                            paddingHorizontal: spaces.spacingMd,
                            paddingBottom: spaces.spacingMd,
                            paddingTop: spaces.spacingXl,
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    <Text
                        style={stylesheet.cardTitle}
                        variant="titleMediumSize"
                    >
                        {event.title}
                    </Text>
                    <Text
                        style={[
                            stylesheet.cardDescription,
                            {
                                color: colors.content.text.mid
                            }
                        ]}
                        variant="bodyMediumSize"
                        numberOfLines={2}
                    >
                        {event.description}
                    </Text>
                    <View
                        style={[
                            stylesheet.memberBadgeRow,
                            {
                                marginTop: spaces.spacingXs
                            }
                        ]}
                    >
                        {
                            event.isOnlyMembers ?
                                <Sticker
                                    title={localize("events-only-members-badge")}
                                    spreadBehaviour="free"
                                    type="warning"
                                    size="small"
                                />
                            :
                                null
                        }
                    </View>
                    <View
                        style={[
                            stylesheet.cardFooter,
                            {
                                marginTop: spaces.spacingMd
                            }
                        ]}
                    >
                        <Button
                            onPress={() => {
                                navigation.navigate("EventEdit", {
                                    eventID: event.eventID
                                });
                            }}
                            icon={({
                                color,
                                size
                            }) => <PenIcon
                                color={colors.content.icon[color]}
                                size={size}
                            />}
                            title={localize("events-edit-button")}
                            customBorderColor="secondaryButton"
                            customColor="secondaryButton"
                            spreadBehaviour="stretch"
                            variant="filled"
                        />
                    </View>
                </View>
            </TouchableOpacity>
        </View>;
    };

    const renderContent = () => {
        if(loading) {
            return <View
                style={[
                    stylesheet.emptyContainer,
                    {
                        padding: spaces.spacingXl
                    }
                ]}
            >
                <ActivityIndicator
                    color={colors.project.gold}
                    size="large"
                />
            </View>;
        }

        if(filteredEvents.length === 0) {
            return <View
                style={[
                    stylesheet.emptyContainer,
                    {
                        padding: spaces.spacingXl
                    }
                ]}
            >
                <Text
                    style={[
                        stylesheet.emptyText,
                        {
                            color: colors.content.text.mid
                        }
                    ]}
                    variant="bodyLargeSize"
                >
                    {localize("events-empty-list")}
                </Text>
            </View>;
        }

        return <View
            style={[
                stylesheet.gridContainer,
                {
                    marginTop: spaces.spacingLg,
                    gap: spaces.spacingLg
                }
            ]}
        >
            {filteredEvents.map(renderCard)}
        </View>;
    };

    return <PageContainer
        isScrollable={true}
    >
        <View
            style={[
                stylesheet.container,
                {
                    padding: spaces.spacingXl
                }
            ]}
        >
            {renderHeader()}
            {renderFilters()}
            {renderContent()}
        </View>
    </PageContainer>;
};

export default EventsList;
